import { Controller, useForm } from 'react-hook-form';

import { useOnboardingContext } from '@/contexts/onboardingContext';
import useTranslate from '@/hooks/useTranslate';
import GradientBg from '@/pages/members/pages/Onboarding/components/GradientBg';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import Card from '@/ui/members/components/Card';
import FieldError from '@/ui/members/components/form/FieldError';
import Input from '@/ui/members/components/form/Input';
import InputGroup from '@/ui/members/components/form/InputGroup';
import Heading from '@/ui/members/components/typography/Heading';
import UL from '@/ui/members/components/typography/UL';

import getSubmittedData from '../utils/getSubmittedData';

import { StepProps } from './types';

interface FirstNameFormValues extends Record<string, string> {
  firstName: string;
}

const FirstNameStep = ({ nodeType, uid }: StepProps) => {
  const onboardingContext = useOnboardingContext();
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.FirstNameStep`);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<FirstNameFormValues>({
    mode: 'onSubmit',
    defaultValues: (getSubmittedData(
      nodeType,
      uid,
      onboardingContext.steps
    ) as FirstNameFormValues) || {
      firstName: ''
    }
  });

  return (
    <StepComponent handleSubmit={handleSubmit} uid={uid} nodeType={nodeType} isValid={isValid}>
      <div className="text-center px-4 mb-6">
        <Heading value={1} className="mb-3">
          {t('aNewBeginning')}
        </Heading>

        <p className="mb-3">{t('youreTakingAnImportantStep')}</p>
      </div>

      <div className="mb-6">
        <GradientBg>
          <Card>
            <p>{t('pleaseCompleteThisQuickForm')}</p>

            <UL
              boldItems
              listItems={[
                t('nameAndEmail'),
                t('locationAndInsuranceProvider'),
                t('yourTreatmentNeedsAndGoals')
              ]}
            />
          </Card>
        </GradientBg>
      </div>

      <p className="relative italic text-sm text-center mb-6">
        {t('thisShouldTakeAboutTwoMinutes')}
      </p>

      <Heading className="relative mb-6 text-center" value={2}>
        {t('letsGetStarted')}
      </Heading>

      <div className="z-10 relative mb-6 flex-flex-col gap-4">
        <InputGroup>
          <Controller
            defaultValue=""
            name="firstName"
            control={control}
            rules={{
              required: t('firstNameIsRequired')
            }}
            render={({ field }) => (
              <>
                <Input
                  autoComplete="given-name"
                  autoFocus
                  label={t('whatsYourName')}
                  id="first-name"
                  onChange={(e) => {
                    field.onChange(e.target.value);
                  }}
                  placeholder={t('enterYourFirstName')}
                  type="text"
                  value={field.value}
                />
              </>
            )}
          />

          <FieldError error={errors.firstName} />
        </InputGroup>
      </div>
    </StepComponent>
  );
};

export default FirstNameStep;
